class RSSParser {
    async parse(rssUrl) {
        try {
            return await this.parseDirectly(rssUrl);
        } catch (error) {
            try {
                return await this.parseWithRss2Json(rssUrl);
            } catch (fallbackError) {
                console.error('Ambos os métodos falharam:', fallbackError);
                return {
                    status: 'error',
                    message: 'Não foi possível carregar o feed RSS'
                };
            }
        }
    }

    async parseDirectly(rssUrl) {
        const response = await fetch(rssUrl);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const xmlText = await response.text();

        // Parse do XML
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        // Verifica se há erros no parsing
        const parserError = xmlDoc.querySelector('parsererror');
        if (parserError) {
            throw new Error('Erro ao fazer parse do XML');
        }

        // Extrai informações do canal
        const channel = xmlDoc.querySelector('channel');
        if (!channel) {
            throw new Error('Canal não encontrado no RSS');
        }

        const feed = {
            title: this.getTextContent(channel, 'title'),
            description: this.getTextContent(channel, 'description'),
            link: this.getTextContent(channel, 'link'),
            image: this.getImage(channel),
        };

        // Extrai os itens (episódios)
        const items = Array.from(xmlDoc.querySelectorAll('item')).map(item => {
            const duration = item.querySelector('itunes\\:duration, duration')?.textContent ||
                item.querySelector('enclosure')?.getAttribute('duration');

            // Tenta obter imagem de múltiplas fontes
            const thumbnail = this.getThumbnail(item) || this.getItemImage(item) || feed.image;

            return {
                title: this.getTextContent(item, 'title'),
                guid: this.getTextContent(item, 'guid'),
                description: this.getTextContent(item, 'description'),
                link: this.getTextContent(item, 'link'),
                pubDate: this.getTextContent(item, 'pubDate'),
                enclosure: this.getEnclosure(item),
                thumbnail: thumbnail,
                duration // Usando a duração extraída
            };
        });

        return {
            status: 'ok',
            feed,
            items
        };
    }

    async parseWithRss2Json(rssUrl) {
        const RSS_TO_JSON_API = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

        const response = await fetch(RSS_TO_JSON_API);
        const data = await response.json();

        if (data.status !== 'ok') {
            throw new Error(data.message || 'Erro ao carregar feed RSS via rss2json');
        }

        // Adiciona duração aos items se não existir
        const itemsWithDuration = data.items.map(item => ({
            ...item,
            duration: item.duration || item.itunes_duration || item.enclosure?.duration || ''
        }));

        return {
            ...data,
            items: itemsWithDuration
        };
    }

    getTextContent(element, tagName) {
        const tag = element.querySelector(tagName);
        return tag ? tag.textContent : '';
    }

    getImage(channel) {
        // Tenta diferentes formatos de imagem
        const itunesImage = channel.querySelector('image[href]');
        if (itunesImage) return itunesImage.getAttribute('href');

        const channelImage = channel.querySelector('image url');
        if (channelImage) return channelImage.textContent;

        const itunesImageTag = channel.querySelector('itunes\\:image, image[href]');
        if (itunesImageTag) return itunesImageTag.getAttribute('href');

        return '';
    }

    getEnclosure(item) {
        const enclosure = item.querySelector('enclosure');
        if (enclosure) {
            return {
                link: enclosure.getAttribute('url'),
                type: enclosure.getAttribute('type'),
                length: enclosure.getAttribute('length')
            };
        }
        return null;
    }

    getThumbnail(item) {
        // Tenta diferentes tags de thumbnail/imagem

        // 1. itunes:image - método mais robusto para múltiplos navegadores
        let itunesImage = item.querySelector('itunes\\:image');
        if (!itunesImage) {
            // Fallback para getElementsByTagName que funciona melhor com namespaces
            const itunesImages = item.getElementsByTagName('itunes:image');
            if (itunesImages.length > 0) {
                itunesImage = itunesImages[0];
            }
        }
        if (itunesImage) {
            const href = itunesImage.getAttribute('href');
            if (href) return href;
        }

        // 2. media:thumbnail
        const mediaThumbnail = item.querySelector('media\\:thumbnail');
        if (mediaThumbnail) {
            return mediaThumbnail.getAttribute('url');
        }

        // 3. media:content com imagem
        const mediaContent = item.querySelector('media\\:content[medium="image"]');
        if (mediaContent) {
            return mediaContent.getAttribute('url');
        }

        // 4. Qualquer media:content com type começando com "image/"
        const mediaContentImage = item.querySelector('media\\:content[type^="image/"]');
        if (mediaContentImage) {
            return mediaContentImage.getAttribute('url');
        }

        // 5. Enclosure com tipo de imagem
        const enclosure = item.querySelector('enclosure[type^="image/"]');
        if (enclosure) {
            return enclosure.getAttribute('url');
        }

        // 6. Tag image direta
        const imageTag = item.querySelector('image');
        if (imageTag) {
            const urlTag = imageTag.querySelector('url');
            return urlTag ? urlTag.textContent : imageTag.textContent;
        }

        return '';
    }

    getItemImage(item) {
        // Tenta várias formas de obter imagem do item

        // 1. media:group > media:content
        const mediaGroup = item.querySelector('media\\:group media\\:content[medium="image"]');
        if (mediaGroup) return mediaGroup.getAttribute('url');

        // 2. googleplay:image
        const googlePlayImage = item.querySelector('googleplay\\:image');
        if (googlePlayImage) return googlePlayImage.getAttribute('href') || googlePlayImage.textContent;

        // 3. Qualquer tag com "image" no namespace
        const anyImage = item.querySelector('[*|image]');
        if (anyImage) return anyImage.getAttribute('href') || anyImage.getAttribute('url');

        return '';
    }

    getDuration(item) {
        // Extrai a duração de diferentes possíveis locais
        return item.querySelector('itunes\\:duration, duration')?.textContent ||
            item.querySelector('enclosure')?.getAttribute('duration') ||
            '';
    }
}

export default RSSParser;